/**
 * The chat box.
 *
 * Deliberately small and out of the way. It only takes keyboard focus when the
 * player asks for it — a chat box that steals focus turns W into a letter
 * instead of a step forward, which is the most annoying bug in any browser game.
 */

export interface ChatMessage {
  id: string;
  name: string;
  body: string;
  at: string;
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`missing element #${id}`);
  return node as T;
}

const MAX_LINES = 60;

export class ChatBox {
  private readonly log: HTMLElement;
  private readonly input: HTMLInputElement;
  private readonly panel: HTMLElement;
  private send: (body: string) => void;

  constructor(send: (body: string) => void) {
    this.log = el('chat-log');
    this.input = el<HTMLInputElement>('chat-input');
    this.panel = el('chat-panel');
    this.send = send;
    this.wire();
  }

  get focused(): boolean {
    return document.activeElement === this.input;
  }

  private wire(): void {
    this.input.addEventListener('keydown', (e) => {
      // Stop movement keys reaching the game while typing.
      e.stopPropagation();

      if (e.key === 'Enter') {
        const body = this.input.value.trim();
        this.input.value = '';
        this.input.blur();
        if (body !== '') this.send(body);
      } else if (e.key === 'Escape') {
        this.input.value = '';
        this.input.blur();
      }
    });

    el('chat-open').addEventListener('click', () => this.focus());
  }

  focus(): void {
    this.panel.classList.add('active');
    this.input.focus();
  }

  add(message: ChatMessage): void {
    const wasAtBottom =
      this.log.scrollTop + this.log.clientHeight >= this.log.scrollHeight - 24;

    const line = document.createElement('div');
    line.className = 'chat-line';

    const who = document.createElement('span');
    who.className = 'chat-who';
    who.textContent = message.name + ':';

    const body = document.createElement('span');
    // textContent, never innerHTML — this is text from another player, and the
    // one thing it must never be able to do is run.
    body.textContent = ' ' + message.body;

    line.append(who, body);

    // A report button, so moderation is one tap rather than a support email.
    const report = document.createElement('button');
    report.type = 'button';
    report.className = 'chat-report';
    report.textContent = '⚑';
    report.title = 'Report this message';
    report.addEventListener('click', () => {
      this.onReport?.(message.id);
      report.disabled = true;
      report.textContent = '✓';
    });
    line.append(report);

    this.log.append(line);

    while (this.log.childElementCount > MAX_LINES) this.log.firstElementChild?.remove();

    // Only auto-scroll if they were already at the bottom, so reading back
    // through the log is not yanked away every time somebody speaks.
    if (wasAtBottom) this.log.scrollTop = this.log.scrollHeight;
  }

  system(text: string): void {
    const line = document.createElement('div');
    line.className = 'chat-line system';
    line.textContent = text;
    this.log.append(line);
    this.log.scrollTop = this.log.scrollHeight;
  }

  history(messages: ChatMessage[]): void {
    this.log.replaceChildren();
    for (const message of messages) this.add(message);
  }

  onReport: ((messageId: string) => void) | null = null;
}
